/** Conflict log with the resolution chosen for each (index-aligned with the conflicts). */
import { useMemo, useState } from 'react';
import { useUI } from '@/state/store';
import { Chip } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { RESOLUTION, type ResolutionStrategy } from '../../constants';
import { useLoader } from '../../store';
import { fmtUtc, fmtUtcShort } from '../format';

const STRATEGY_LABEL: Record<ResolutionStrategy, string> = {
  MOVE_TO_ALTERNATE: 'Moved to alternate',
  ACCEPT_REDUCED: 'Reduced pass',
  WAIT_NEXT_PASS: 'Next pass',
  BUMP_LOWER: 'Bumped',
  UNRESOLVED: 'Unresolved',
};
const STRATEGY_TONE: Record<ResolutionStrategy, 'nominal' | 'caution' | 'warning' | 'neutral'> = {
  MOVE_TO_ALTERNATE: 'nominal',
  ACCEPT_REDUCED: 'caution',
  WAIT_NEXT_PASS: 'neutral',
  BUMP_LOWER: 'caution',
  UNRESOLVED: 'warning',
};

export function ConflictsPanel() {
  const dataset = useLoader((s) => s.dataset);
  const results = useLoader((s) => s.results);
  const select = useUI((s) => s.select);
  const [strategy, setStrategy] = useState<ResolutionStrategy | ''>('');

  const rows = useMemo(() => {
    if (!results) return [];
    return results.conflicts
      .map((c, i) => ({ conflict: c, resolution: results.resolutions[i] }))
      .filter((r) => !strategy || r.resolution?.strategy === strategy);
  }, [results, strategy]);

  if (!results) return <div className="p-4 text-xs text-ink-3">Run the analysis to see conflicts.</div>;
  const missionName = (id: string) => dataset?.missions.find((m) => m.id === id)?.shortName ?? id;
  const antennaName = (id: string) => dataset?.antennas.find((a) => a.id === id)?.shortName ?? id;
  const counts = Object.values(RESOLUTION).map((s) => ({
    s,
    n: results.resolutions.filter((r) => r.strategy === s).length,
  }));

  return (
    <div className="flex h-full flex-col text-data">
      <div
        role="group"
        aria-label="Filter by resolution"
        className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-hairline px-panel py-2"
      >
        <button
          type="button"
          aria-pressed={strategy === ''}
          onClick={() => setStrategy('')}
          className={cn(
            'rounded-control px-2 py-0.5',
            strategy === '' ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:text-ink',
          )}
        >
          All {results.conflicts.length}
        </button>
        {counts
          .filter((c) => c.n > 0)
          .map((c) => (
            <button
              key={c.s}
              type="button"
              aria-pressed={strategy === c.s}
              onClick={() => setStrategy(c.s)}
              className={cn(
                'rounded-control px-2 py-0.5',
                strategy === c.s ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:text-ink',
              )}
            >
              {STRATEGY_LABEL[c.s]} {c.n}
            </button>
          ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        {rows.length === 0 ? (
          <div className="p-4 text-ink-3">
            No conflicts{strategy ? ' with this resolution' : ' in the window'}.
          </div>
        ) : (
          <ul>
            {rows.map(({ conflict: c, resolution: r }) => (
              <li key={c.id} className="border-b border-hairline/60 px-panel py-cell-y">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="text-ink hover:text-accent"
                    onClick={() => select({ missionId: c.missionB, antennaId: null, assetId: null })}
                  >
                    {missionName(c.missionB)}
                  </button>
                  <span className="text-ink-3">blocked by</span>
                  <button
                    type="button"
                    className="text-ink hover:text-accent"
                    onClick={() => select({ missionId: c.missionA, antennaId: null, assetId: null })}
                  >
                    {missionName(c.missionA)}
                  </button>
                  <span className="text-ink-3">on</span>
                  <button
                    type="button"
                    className="text-ink hover:text-accent"
                    onClick={() => select({ antennaId: c.antennaId, missionId: null, assetId: null })}
                  >
                    {antennaName(c.antennaId)}
                  </button>
                  <span className="font-mono text-ink-3">
                    {fmtUtc(c.overlapStartMs)} to {fmtUtcShort(c.overlapEndMs, c.overlapStartMs)},{' '}
                    {c.blockedMinutes} min
                  </span>
                  {r && (
                    <Chip
                      tone={STRATEGY_TONE[r.strategy as ResolutionStrategy] ?? 'neutral'}
                      className="ml-auto"
                    >
                      {STRATEGY_LABEL[r.strategy as ResolutionStrategy] ?? r.strategy}
                    </Chip>
                  )}
                </div>
                {r && (
                  <div className="mt-0.5 text-ink-2">
                    {r.description}
                    {r.newAntennaId ? ` Now on ${antennaName(r.newAntennaId)}.` : ''}
                    {r.dataCapture > 0 && r.dataCapture < 1
                      ? ` ${Math.round(r.dataCapture * 100)}% of the data captured.`
                      : ''}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
